/**
 * Database API Layer for Cloudflare Workers
 * 
 * Unified API layer for database operations using Workers backend.
 * All functionality uses Cloudflare Workers with D1 Database.
 * 
 * API Endpoints:
 * - Authentication: POST https://api.trafomatic.reg2dep.net/api/auth/telegram
 * - User Profile: GET https://api.trafomatic.reg2dep.net/api/users/:id
 * - Other endpoints: See workers/src/index.ts
 */

import { workersClient } from '@/lib/workers-api';

// ===== TYPE DEFINITIONS =====

/**
 * User profile data
 */
export interface User {
  id: string;
  telegram_id: string;
  role: 'ADMIN' | 'SUPPORT' | 'MANAGER' | 'WEBMASTER';
  profile_nickname?: string;
  telegram_username?: string;
  telegram_first_name?: string;
  telegram_last_name?: string;
  language_code?: string;
  is_verified: boolean;
  status: 'active' | 'banned' | 'suspended' | 'deleted' | 'pending';
  created_at: string;
  updated_at: string;
}

/**
 * User profile updates
 */
export type UserProfileUpdate = Partial<Omit<User, 'id' | 'created_at' | 'updated_at'>>;

/**
 * Database type stub for legacy compatibility
 */
export type Database = Record<string, unknown>;

// ===== API CLIENT =====

const WORKERS_API_URL = import.meta.env.VITE_API_URL || 'https://api.trafomatic.reg2dep.net';

// Helper to get auth token
function getAuthToken(): string | null {
  return localStorage.getItem('workers_access_token');
}

// Export Workers client for database operations
export const db = workersClient;
export const dbClient = workersClient;

// Legacy exports for backward compatibility
export const supabase = workersClient;
export const supabaseClient = workersClient;

// User API with Workers backend
export const userApi = {
  getUserProfile: async (userId: string | number): Promise<User | null> => {
    const token = getAuthToken();
    const response = await fetch(`${WORKERS_API_URL}/api/users/${userId}`, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        return null; // User not found
      }
      throw new Error(`Failed to fetch user profile: ${response.statusText}`);
    }
    
    return await response.json() as User;
  },
  
  updateProfile: async (userId: string | number, updates: UserProfileUpdate): Promise<User> => {
    const token = getAuthToken();
    
    // Use Workers universal DB API: PATCH /api/db/users?id_eq={userId}
    const response = await fetch(`${WORKERS_API_URL}/api/db/users?id_eq=${userId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[userApi.updateProfile] Error response:', errorText);
      throw new Error(`Failed to update user profile: ${response.statusText}`);
    }
    
    const result = await response.json();
    // Workers DB API returns { data: {...}, error: null }
    return result.data as User;
  },
};

// ===== REMOVED LEGACY EXPORTS =====
// offerApi, referralApi, authApi, callEdgeFunction, updateUserRole, ApiErrorHandler
// All removed - use workersClient.from() or direct Workers API calls instead
